/**
 * Evaluate Open when / product-trigger / ecommerce rules for static
 * product page upsell blocks (PDP theme slot).
 *
 * Mirrors cart-drawer-upsell-conditions.js, but against the current product
 * page product (not cart lines).
 */

import {
  evaluateCondition,
  buildCartInfoFromCart,
} from "./cart-drawer-upsell-conditions.js";

function normalizeId(id) {
  if (id == null || id === "") return "";
  const raw = String(id);
  return raw.includes("/") ? raw.split("/").pop() : raw;
}

function parseTags(tags) {
  if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
  if (typeof tags === "string" && tags.trim()) {
    return tags.split(",").map((t) => t.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Normalize Shopify product /products/{handle}.js (or meta.product) into
 * the shape used by product_trigger_* / ecommerce evaluators.
 */
export function normalizeProductPageContext(product) {
  if (!product || typeof product !== "object") return null;

  const priceRaw = product.price ?? product.price_min ?? 0;
  const price =
    Number(priceRaw) > 1000 ? Number(priceRaw) / 100 : Number(priceRaw) || 0;

  return {
    id: product.id ?? product.product_id,
    title: product.title ?? "",
    product_type: product.product_type ?? product.type ?? "",
    vendor: product.vendor ?? "",
    tags: parseTags(product.tags),
    available: product.available !== false,
    price,
    handle: product.handle ?? "",
  };
}

function ruleResult(passed, reason, action) {
  return { passed, reason, action };
}

function checkProductMatch(condition, productData) {
  if (!condition) return true;

  const offerTriggered = condition.offer_is_triggered;
  if (offerTriggered?.type) {
    const { type, value } = offerTriggered;
    switch (type) {
      case "all_product":
        return true;
      case "all_except":
        return !!(
          productData &&
          Array.isArray(value) &&
          !value.some((p) => normalizeId(p.id) === normalizeId(productData.id))
        );
      case "specific_product":
        return !!(
          productData &&
          Array.isArray(value) &&
          value.some((p) => normalizeId(p.id) === normalizeId(productData.id))
        );
      case "product_specific_title":
        return !!(
          productData?.title &&
          Array.isArray(value) &&
          value.some((val) => productData.title.includes(val))
        );
      case "product_specific_tag":
        return !!(
          productData &&
          Array.isArray(productData.tags) &&
          Array.isArray(value) &&
          productData.tags.some((tag) => value.includes(tag))
        );
      case "product_specific_vendor":
        return !!(
          productData?.vendor &&
          Array.isArray(value) &&
          value.includes(productData.vendor)
        );
      case "shopify_recommended":
      case "ai_recommended":
        return !!productData;
      default:
        return true;
    }
  }

  return true;
}

function evaluateProductTriggerRule(field, operator, value, action, productData) {
  if (!productData) {
    if (field === "product_trigger_all") {
      const shouldShow =
        value === true || value === "true" || value === "True (All Products)";
      const passed = action === "show" ? shouldShow : !shouldShow;
      return ruleResult(
        passed,
        passed ? "All products trigger allowed" : "All products trigger blocked",
        action
      );
    }
    return ruleResult(false, "Not on product page", action);
  }

  let fieldValue;
  switch (field) {
    case "product_trigger_all":
      fieldValue = true;
      break;
    case "product_trigger_specific":
    case "product_trigger_except":
      fieldValue = normalizeId(productData.id);
      break;
    case "product_trigger_title":
      fieldValue = productData.title || "";
      break;
    case "product_trigger_tag":
      fieldValue = Array.isArray(productData.tags) ? productData.tags : [];
      break;
    case "product_trigger_vendor":
      fieldValue = productData.vendor || "";
      break;
    default:
      return ruleResult(true, "Unknown product trigger", action);
  }

  if (!operator) {
    switch (field) {
      case "product_trigger_all": {
        const shouldShow =
          value === true || value === "true" || value === "True (All Products)";
        return ruleResult(
          shouldShow,
          shouldShow ? "All products" : "Not all products",
          action
        );
      }
      case "product_trigger_specific": {
        const currentId = normalizeId(productData.id);
        const isInSpecific =
          Array.isArray(value) &&
          value.some((p) => normalizeId(p.id) === currentId);
        return ruleResult(
          isInSpecific,
          isInSpecific ? "In specific list" : "Not in list",
          action
        );
      }
      case "product_trigger_except": {
        const currentId = normalizeId(productData.id);
        const isInExcept =
          Array.isArray(value) &&
          value.some((p) => normalizeId(p.id) === currentId);
        return ruleResult(
          !isInExcept,
          !isInExcept ? "Not in except list" : "In except list",
          action
        );
      }
      case "product_trigger_title": {
        const titleMatch =
          Array.isArray(value) &&
          value.some((val) => productData.title && productData.title.includes(val));
        return ruleResult(
          titleMatch,
          titleMatch ? "Title match" : "Title mismatch",
          action
        );
      }
      case "product_trigger_tag": {
        const tagMatch =
          Array.isArray(value) &&
          Array.isArray(productData.tags) &&
          value.some((val) => productData.tags.includes(val));
        return ruleResult(tagMatch, tagMatch ? "Tag match" : "Tag mismatch", action);
      }
      case "product_trigger_vendor": {
        const vendorMatch =
          Array.isArray(value) && value.includes(productData.vendor);
        return ruleResult(
          vendorMatch,
          vendorMatch ? "Vendor match" : "Vendor mismatch",
          action
        );
      }
      default:
        return ruleResult(true, "Unknown trigger", action);
    }
  }

  if (field === "product_trigger_specific" && Array.isArray(value)) {
    const normalizedValue = value.map((p) => normalizeId(p.id));
    const passed = evaluateCondition(fieldValue, operator, normalizedValue);
    return ruleResult(
      passed,
      passed ? "Specific product match" : "Specific product mismatch",
      action
    );
  }

  if (field === "product_trigger_except" && Array.isArray(value)) {
    const normalizedValue = value.map((p) => normalizeId(p.id));
    const passed = !evaluateCondition(fieldValue, operator, normalizedValue);
    return ruleResult(
      passed,
      passed ? "Except list pass" : "Except list fail",
      action
    );
  }

  const passed = evaluateCondition(fieldValue, operator, value);
  return ruleResult(passed, passed ? `${field} passed` : `${field} failed`, action);
}

function evaluateEcommerceDataRule(rule, productData, cartInfo) {
  const { field, operator, value, action } = rule;

  if (field === "cart_total" || field === "cart_product_count") {
    const fieldValue =
      field === "cart_total" ? cartInfo.total : cartInfo.itemCount;
    const passed = evaluateCondition(fieldValue, operator, value);
    return ruleResult(passed, passed ? `${field} passed` : `${field} failed`, action);
  }

  if (!productData) {
    return ruleResult(false, "No product context", action);
  }

  let fieldValue = "";
  switch (field) {
    case "product_name":
      fieldValue = productData.title || "";
      break;
    case "product_price":
      fieldValue = productData.price || 0;
      break;
    case "product_available":
      fieldValue = productData.available || false;
      break;
    case "product_tags":
      fieldValue = productData.tags || [];
      break;
    case "product_type":
      fieldValue = productData.product_type || "";
      break;
    case "product_vendor":
      fieldValue = productData.vendor || "";
      break;
    default:
      return ruleResult(true, "Unknown ecommerce field", action);
  }

  const passed = evaluateCondition(fieldValue, operator, value);
  return ruleResult(passed, passed ? `${field} passed` : `${field} failed`, action);
}

function aggregateRuleResults(results) {
  const hideRules = results.filter((r) => r.action === "hide" && r.passed);
  if (hideRules.length > 0) return false;

  const showRules = results.filter((r) => r.action === "show");
  if (showRules.length > 0) {
    const passedShowRules = showRules.filter((r) => r.passed);
    if (passedShowRules.length === 0) return false;
  }

  return true;
}

/**
 * Product / catalog ecommerce rules for the PDP upsell block.
 * @param {object} popup
 * @param {{ product?: object, cart?: object }} [context]
 * @returns {boolean}
 */
export function checkProductPageUpsellConditions(popup, context = {}) {
  const conditions = popup?.generalSettings?.condition || {};
  const ecommerce = conditions.ecommerce || {};
  const product = conditions.product || {};
  const productData = normalizeProductPageContext(context.product);
  const cartInfo = buildCartInfoFromCart(context.cart || {});

  if (!ecommerce.enabled) {
    return checkProductMatch(product, productData);
  }

  if (!Array.isArray(ecommerce.rules) || ecommerce.rules.length === 0) {
    return checkProductMatch(product, productData);
  }

  const results = ecommerce.rules.map((rule) => {
    if (String(rule?.field || "").startsWith("product_trigger_")) {
      return evaluateProductTriggerRule(
        rule.field,
        rule.operator,
        rule.value,
        rule.action,
        productData
      );
    }
    return evaluateEcommerceDataRule(rule, productData, cartInfo);
  });

  return aggregateRuleResults(results);
}

/**
 * Whether any visitor-timing gate is configured (delay / scroll / exit / …).
 * When none are set, the block is treated as “immediate”.
 */
export function hasProductPageVisitorTimingGate(popup) {
  const trigger = popup?.generalSettings?.condition?.trigger || {};
  return Boolean(
    trigger.allow_show_after ||
      trigger.allow_after_scroll ||
      trigger.allow_Exit_Intent ||
      trigger.allow_after_inactive ||
      trigger.allow_onclick ||
      trigger.allow_onhover
  );
}

/**
 * Combined gate used by the product-page runtime.
 * Visitor timing is handled separately by the runtime (listeners / delay).
 */
export function shouldShowProductPageUpsell(popup, context = {}) {
  if (!popup) return false;
  if (popup.is_active === false) return false;
  if (popup.is_published === false) return false;
  return checkProductPageUpsellConditions(popup, context);
}
